import { criteriaSentence } from '../js/criteria-phrase'

// Quick-book helpers for handyman jobs picked from the price list: the
// "Find a Service" sheet (HandymanDealItems), the HandymanBooking tiles and
// the cards on /home/services/handyman.
//
// How the backend prices a request (HandymanCostCalculator.calculateServicingCost):
//  - it looks up the job name in whichever of criteria keys 2, 31, 32, 33 or 4
//    is present,
//  - key "5" (number of units) multiplies that price,
//  - key "1" only sets the service-type label, e.g. "Electrical (est.)".
// So quick book doesn't need the wizard's area and sub-question codes. Every
// job goes as { 1: type, 2: job name, 5: units }. The per-category switch
// statements this replaces broke when the price list moved to new category
// names, and could never book Drilling & Installation or Painting jobs.

const HANDYMAN = 'Handyman Services'

// Key "1" label per price-list category. utils/service-availability.js
// pauses by this label (e.g. 'Plumbing'), and the wizard uses the same values
// for its first question. Old category names are kept until the backend
// change reaches production.
const TYPE_BY_CATEGORY = {
    'Electrical': 'Electrical',
    'Plumbing': 'Plumbing',
    'Kitchen': 'Plumbing',
    'Bathroom': 'Plumbing',
    'Bedroom': 'Plumbing',
    'Doors, Cabinets & Furniture': 'Door, cupboard/cabinet',
    'Door/Cabinet': 'Door, cupboard/cabinet',
    'Drilling & Installation': 'Drilling & Installation',
    'Drilling/Installation': 'Drilling & Installation',
    'Painting': 'Painting',
}

export const serviceTypeFor = (category) => TYPE_BY_CATEGORY[category] || category

// The wizard's "Specify the number of units." question, if this job leads to
// it in GetServiceCategorySettings. Only used when the price list has no
// unitRequired field (the production API before the backend update).
export const getUnitsQuestion = (requestCategorySettings, itemName) => {
    const settings = (requestCategorySettings || []).find((c) => c.requestType === HANDYMAN)
    const questions = settings?.criteriaQuestions || []

    for (const q of questions) {
        const choice = (q.choices || []).find((ch) => ch.value === itemName)
        if (!choice) continue
        const next = questions.find((x) => x.question_code === choice.next_question_code)
        if (next?.inputType === 'number') {
            return { questionCode: next.question_code, question: next.question }
        }
    }
    return null
}

/** Whether to ask for a unit count. The price list's unitRequired wins; the settings are the fallback. */
export const isUnitRequired = (item, requestCategorySettings) =>
    typeof item?.unitRequired === 'boolean'
        ? item.unitRequired
        : !!getUnitsQuestion(requestCategorySettings, item?.itemName)

const UNITS_CODE = '5'

/**
 * Request criteria for a quick-booked job.
 * @param {{ itemName: string, category: string }} item  a price-list entry
 * @param {number|null} units  unit count, or null when units aren't asked
 */
export const buildQuickBookCriteria = (item, units = null) => {
    const type = serviceTypeFor(item.category)
    const criteria = [
        { 1: type, criteria: criteriaSentence('1', HANDYMAN, type) },
        { 2: item.itemName, criteria: item.itemName },
    ]
    if (units != null) {
        const value = String(units)
        criteria.push({ [UNITS_CODE]: value, criteria: criteriaSentence(UNITS_CODE, HANDYMAN, value) })
    }
    return criteria
}
